import { useEffect, useState } from 'react';
import { CalendarDays, User, Check, Users } from 'lucide-react';
import { getMyClasses, getCatalog, MyClass, CatalogClass, WEEKDAYS_SHORT, hhmm } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { colorClase } from '../../../utils/classColor';

type View = 'mias' | 'todas';

function Schedule({ blocks }: { blocks: { weekday: number; start_time: string; end_time: string }[] }) {
  if (!blocks.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {blocks.map((s, i) => (
        <span key={i} className="rounded-full bg-surface-2 px-2.5 py-1 text-xs text-content-2">
          {WEEKDAYS_SHORT[s.weekday]} {hhmm(s.start_time)}–{hhmm(s.end_time)}
        </span>
      ))}
    </div>
  );
}

export default function StudentClasses() {
  const [view, setView] = useState<View>('mias');
  const [mias, setMias] = useState<MyClass[] | null>(null);
  const [todas, setTodas] = useState<CatalogClass[] | null>(null);

  useEffect(() => { getMyClasses().then((r) => setMias(r.data)).catch(() => setMias([])); }, []);
  useEffect(() => {
    if (view === 'todas' && todas === null) getCatalog().then((r) => setTodas(r.data)).catch(() => setTodas([]));
  }, [view, todas]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-xl font-bold text-content">Clases</h1>
        {/* Toggle Mis clases / Todas */}
        <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
          {(['mias', 'todas'] as View[]).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`rounded-md px-3 py-1.5 font-medium transition ${view === v ? 'bg-accent-solid text-on-accent' : 'text-content-2 hover:text-content'}`}>
              {v === 'mias' ? 'Mis clases' : 'Todas'}
            </button>
          ))}
        </div>
      </div>

      {view === 'mias' ? (
        !mias ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : mias.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No estás inscrito en clases"
            subtitle="Toca “Todas” para ver lo que ofrece el estudio." />
        ) : (
          <div className="space-y-3">
            {mias.map((c) => (
              <div key={c.class_id} className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 h-10 w-1.5 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-content">{c.name}</div>
                    {c.style && <div className="text-sm text-content-2">{c.style}</div>}
                    {c.teacher && <div className="mt-0.5 flex items-center gap-1 text-xs text-content-3"><User size={12} /> {c.teacher}</div>}
                    <Schedule blocks={c.schedule} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // ── TODAS las clases (catálogo, solo-ver) ──
        !todas ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
        ) : todas.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No hay clases publicadas" />
        ) : (
          <>
            <p className="text-sm text-content-3">Explora todas las clases del estudio. Para inscribirte a una nueva, escríbenos o pregunta en recepción.</p>
            <div className="space-y-3">
              {todas.map((c) => (
                <div key={c.class_id} className={`rounded-2xl border bg-surface p-4 shadow-sm ${c.enrolled_me ? 'border-accent/50' : 'border-line'}`}>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 h-10 w-1.5 shrink-0 rounded-full" style={{ background: colorClase(c.color) }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display font-bold text-content">{c.name}</span>
                        {c.enrolled_me && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent-ink">
                            <Check size={12} /> Ya inscrito
                          </span>
                        )}
                      </div>
                      {c.style && <div className="text-sm text-content-2">{c.style}</div>}
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-content-3">
                        {c.teacher && <span className="inline-flex items-center gap-1"><User size={12} /> {c.teacher}</span>}
                        <span className="inline-flex items-center gap-1"><Users size={12} /> {c.enrolled}{c.capacity ? `/${c.capacity}` : ''}</span>
                      </div>
                      <Schedule blocks={c.schedule} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>
  );
}
